import { useQuery } from '@tanstack/react-query'
import { accountFromAggregateMe, fetchAggregateMe } from '@/api/aggregateMe'
import { apiFetch, getCallerAgentId, hasLiveApi } from '@/api/client'
import { agentNodeToAccount, type AgentNode } from '@/api/map/agent'
import { decodeClaims, getAccessToken, isDemoSession } from '@/auth/oidc'
import * as api from '@/data/api'
import type { Account } from '@/data/types'
import { keys } from './keys'

export interface SessionView {
  session: Awaited<ReturnType<typeof api.getSession>>
  account: Account
}

function scopesFromClaims(who: ReturnType<typeof decodeClaims>) {
  return [
    ...(who?.roles.includes('ops') ? (['ops'] as const) : []),
    ...(who?.roles.includes('risk-desk') ? (['risk-desk'] as const) : []),
  ]
}

/** Agent seat — hierarchy node for JWT `agentId`. */
async function loadAgentSession(agentId: string): Promise<SessionView> {
  const node = await apiFetch<AgentNode>(`/v1/agents/${agentId}`)
  const account = agentNodeToAccount(node)
  const who = decodeClaims(getAccessToken())
  return {
    session: {
      accountId: account.id,
      username: who?.preferredUsername ?? account.username,
      displayName: account.displayName,
      role: account.role,
      scopes: scopesFromClaims(who),
    },
    account,
  }
}

/**
 * Aggregate group — Lab Aggregate identity via `/v1/me` (memberList attach).
 * Same probe AuthGate already uses; Detail Book live gate needs this session.
 */
async function loadAggregateSession(): Promise<SessionView> {
  const me = await fetchAggregateMe()
  if (!me.ok || !me.caller.aggregateGroupId?.trim()) {
    throw new Error('Aggregate session could not be loaded')
  }
  if (me.caller.accessLevel !== 'Aggregate') {
    throw new Error('Signed in caller is not an Aggregate group')
  }
  const account = accountFromAggregateMe(me.caller)
  const who = decodeClaims(getAccessToken())
  return {
    session: {
      accountId: account.id,
      username: who?.preferredUsername ?? account.username,
      displayName: account.displayName,
      role: account.role,
      scopes: scopesFromClaims(who),
    },
    account,
  }
}

export async function loadSession(): Promise<SessionView> {
  if (hasLiveApi()) {
    const agentId = getCallerAgentId()
    if (agentId) return loadAgentSession(agentId)
    // Aggregate JWT: realm role `aggregate`, no agentId — AuthGate /v1/me path.
    return loadAggregateSession()
  }

  if (!isDemoSession() && getAccessToken() && !getCallerAgentId()) {
    throw new Error(
      'Signed in but no agentId on the token — bind agentId on the Keycloak user (npm run keycloak:seed)',
    )
  }

  const session = await api.getSession()
  const account = await api.getAccount(session.accountId)
  if (!account) throw new Error('Your account could not be loaded')
  return { session, account }
}

export function useSession() {
  return useQuery({
    queryKey: keys.session,
    queryFn: loadSession,
    staleTime: 60_000,
    // 404 / missing Aggregate me must not hammer hierarchy — gate shows an error instead.
    retry: false,
    refetchOnWindowFocus: false,
  })
}
